import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useUsuario } from '../../contexts/UsuarioContext';
import { useDepartamento } from '../../contexts/DepartamentoContext';
import chatInternoService, { ChatConversa, ChatMensagem } from '../../services/chatInternoService';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useTheme } from '../../contexts/ThemeContext';
import { toast } from 'react-toastify';

interface Props {
  onFechar: () => void;
  fullPage?: boolean;
  onNaoLidasChange?: (total: number) => void;
}

// ── Helpers de avatar ──────────────────────────────────────────────────────────

const getAvatarSrc = (avatar?: string | null): string | null => {
  if (!avatar) return null;
  if (avatar.startsWith('data:') || avatar.startsWith('http')) return avatar;
  return null;
};

const getInitials = (nome: string): string => {
  const parts = nome.trim().split(' ').filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0]?.slice(0, 2) || '??').toUpperCase();
};

const AVATAR_COLORS = ['#c0392b', '#2980b9', '#27ae60', '#8e44ad', '#e67e22', '#16a085', '#d35400', '#1a1a2e'];
const getAvatarColor = (id: string): string => {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = id.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

interface AvatarProps { nome: string; avatar?: string | null; id?: string; size?: number; style?: React.CSSProperties; }
const AvatarCircle: React.FC<AvatarProps> = ({ nome, avatar, id, size = 32, style }) => {
  const src = getAvatarSrc(avatar);
  const [imgError, setImgError] = useState(false);
  const color = getAvatarColor(id || nome);
  const baseStyle: React.CSSProperties = { width: size, height: size, borderRadius: '50%', flexShrink: 0, ...style };
  if (src && !imgError) {
    return <img src={src} alt={nome} onError={() => setImgError(true)} style={{ ...baseStyle, objectFit: 'cover' }} />;
  }
  return (
    <div style={{ ...baseStyle, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: size * 0.38, fontWeight: 700 }}>
      {getInitials(nome)}
    </div>
  );
};

// ── Som de notificação ────────────────────────────────────────────────────────

const tocarSomNotificacao = () => {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  } catch {}
};

// ── Tipos ─────────────────────────────────────────────────────────────────────

interface MiniPerfil {
  id: string;
  nome: string;
  cargo?: string;
  departamento_id?: string;
  avatar?: string;
}

// ── Componente principal ──────────────────────────────────────────────────────

// Este componente é todo estilizado via `style={{}}` inline (não Tailwind),
// então o modo escuro não pode ser resolvido por classe CSS — os tokens
// abaixo trocam de valor conforme o tema e são usados em todo o arquivo no
// lugar dos hex fixos de fundo/texto/borda. Cores de marca/avatar (o
// vermelho de destaque, as cores dos círculos de avatar) continuam fixas
// nos dois modos, de propósito.
const useChatCores = () => {
  const { tema } = useTheme();
  const escuro = tema === 'escuro';
  return {
    bg: escuro ? '#1f2937' : '#fff',
    bgPainel: escuro ? '#111827' : '#f9fafb',
    bgSutil: escuro ? '#1f2937' : '#fafafa',
    bgChip: escuro ? '#374151' : '#f3f4f6',
    borda: escuro ? '#374151' : '#e5e7eb',
    bordaSutil: escuro ? '#374151' : '#f0f0f0',
    textoPrincipal: escuro ? '#f3f4f6' : '#111',
    textoSecundario: escuro ? '#d1d5db' : '#333',
    textoTerciario: escuro ? '#9ca3af' : '#555',
    textoQuaternario: escuro ? '#9ca3af' : '#666',
    textoMuted: escuro ? '#6b7280' : '#999',
    textoMuted2: escuro ? '#6b7280' : '#aaa',
    textoMuted3: escuro ? '#6b7280' : '#ccc',
    textoMuted4: escuro ? '#6b7280' : '#888',
    textoMuted5: escuro ? '#6b7280' : '#777',
  };
};

const ChatInterno: React.FC<Props> = ({ onFechar, fullPage = false, onNaoLidasChange }) => {
  const cores = useChatCores();
  const { usuario } = useAuth();
  const { usuarios } = useUsuario();
  const { obterDepartamentoPorId } = useDepartamento();
  const [conversas, setConversas] = useState<ChatConversa[]>([]);
  const [conversaAtual, setConversaAtual] = useState<ChatConversa | null>(null);
  const [mensagens, setMensagens] = useState<ChatMensagem[]>([]);
  const [texto, setTexto] = useState('');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [erroEnvio, setErroEnvio] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  const [mostrarNovaConversa, setMostrarNovaConversa] = useState(false);
  const [mostrarInfoGrupo, setMostrarInfoGrupo] = useState(false);
  const [confirmarExclusao, setConfirmarExclusao] = useState<string | null>(null);
  const [adicionandoMembro, setAdicionandoMembro] = useState(false);
  const [buscaNovoMembro, setBuscaNovoMembro] = useState('');
  const [adicionando, setAdicionando] = useState(false);
  const [novoTipo, setNovoTipo] = useState<'direto' | 'grupo'>('direto');
  const [novoNome, setNovoNome] = useState('');
  const [membrosSelecionados, setMembrosSelecionados] = useState<string[]>([]);
  const [buscaUsuario, setBuscaUsuario] = useState('');
  const [usuariosOnline, setUsuariosOnline] = useState<string[]>([]);
  const [miniPerfil, setMiniPerfil] = useState<MiniPerfil | null>(null);
  const [fotoExpandida, setFotoExpandida] = useState<{ nome: string; avatar?: string; id: string } | null>(null);
  // Busca dentro da conversa
  const [mostrarBusca, setMostrarBusca] = useState(false);
  const [termoBusca, setTermoBusca] = useState('');
  const [indiceBusca, setIndiceBusca] = useState(0);
  const buscaInputRef = useRef<HTMLInputElement>(null);
  const mensagensContainerRef = useRef<HTMLDivElement>(null);
  const mensagensEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Controla para onde rolar após carregar mensagens
  const scrollAncora = useRef<'bottom' | 'primeiro-nao-lido' | null>(null);
  // Timestamp de leitura do usuário no momento em que abriu a conversa
  const minhaLeituraEntradaRef = useRef<string | null>(null);
  // Refs estáveis para o polling (evita reiniciar o intervalo por mudança de referência)
  const conversaAtualRef = useRef(conversaAtual);
  const onNaoLidasChangeRef = useRef(onNaoLidasChange);
  const usuarioIdRef = useRef(usuario?.id);

  const carregarConversas = useCallback(async () => {
    try {
      const lista = await chatInternoService.listarConversas();
      setConversas(lista);
    } catch {}
    setCarregando(false);
  }, []);

  const carregarMensagens = useCallback(async (conversa: ChatConversa) => {
    try {
      const msgs = await chatInternoService.buscarMensagens(conversa.id);
      // Registra a ultima_leitura do usuário no momento de entrar na conversa
      const meuMembro = conversa.membros?.find(m => m.id === usuario?.id);
      minhaLeituraEntradaRef.current = meuMembro?.ultima_leitura ?? null;
      const naoLidas = Number(conversa.nao_lidas) || 0;
      scrollAncora.current = naoLidas > 0 ? 'primeiro-nao-lido' : 'bottom';
      setMensagens(msgs);
      chatInternoService.marcarComoLido(conversa.id).catch(() => {});
      setConversas(prev => {
        const atualizadas = prev.map(c => c.id === conversa.id ? { ...c, nao_lidas: 0 } : c);
        if (onNaoLidasChangeRef.current) {
          const totalNaoLidas = atualizadas.reduce((acc, c) => acc + (Number(c.nao_lidas) || 0), 0);
          onNaoLidasChangeRef.current(totalNaoLidas);
        }
        return atualizadas;
      });
      if (onNaoLidasChangeRef.current) {
        chatInternoService.totalNaoLidas().then(t => onNaoLidasChangeRef.current?.(t)).catch(() => {});
      }
    } catch {}
  }, [usuario?.id]); // onNaoLidasChange lido via ref — não recria a função desnecessariamente

  useEffect(() => { carregarConversas(); }, [carregarConversas]);

  useEffect(() => {
    if (conversaAtual) carregarMensagens(conversaAtual);
  }, [conversaAtual, carregarMensagens]);

  // Scroll inteligente: ancora definida em carregarMensagens, comportamento normal após isso
  useEffect(() => {
    const container = mensagensContainerRef.current;
    if (!container) return;

    if (scrollAncora.current === 'bottom') {
      scrollAncora.current = null;
      container.scrollTop = container.scrollHeight;
    } else if (scrollAncora.current === 'primeiro-nao-lido') {
      scrollAncora.current = null;
      const el = container.querySelector('[data-primeiro-nao-lido="true"]') as HTMLElement | null;
      if (el) {
        el.scrollIntoView({ block: 'start', behavior: 'instant' as ScrollBehavior });
      } else {
        container.scrollTop = container.scrollHeight;
      }
    } else {
      // Comportamento normal: só rola ao fim se já estava perto do fim
      const estaNoFundo = container.scrollHeight - container.scrollTop - container.clientHeight < 200;
      if (estaNoFundo) container.scrollTop = container.scrollHeight;
    }
  }, [mensagens]);

  useEffect(() => {
    const buscar = () => chatInternoService.obterOnline().then(setUsuariosOnline).catch(() => {});
    buscar();
    const interval = setInterval(buscar, 30000);
    return () => clearInterval(interval);
  }, []);

  // ── Busca dentro da conversa ─────────────────────────────────────────────────

  // Índices das mensagens que casam com o termo
  const indicesMensagensBusca = termoBusca.trim().length >= 2
    ? mensagens
        .map((m, i) => ({ m, i }))
        .filter(({ m }) => m.conteudo?.toLowerCase().includes(termoBusca.toLowerCase()))
        .map(({ i }) => i)
    : [];

  const totalResultados = indicesMensagensBusca.length;

  // Reseta busca ao trocar de conversa
  useEffect(() => {
    setTermoBusca('');
    setIndiceBusca(0);
    setMostrarBusca(false);
  }, [conversaAtual?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Foca o input quando a barra abre
  useEffect(() => {
    if (mostrarBusca) buscaInputRef.current?.focus();
  }, [mostrarBusca]);

  // Reseta índice quando o termo muda
  useEffect(() => {
    setIndiceBusca(0);
  }, [termoBusca]);

  // Scrolla até o resultado atual
  useEffect(() => {
    if (totalResultados === 0) return;
    const container = mensagensContainerRef.current;
    if (!container) return;
    const alvo = container.querySelector(`[data-busca-idx="${indicesMensagensBusca[indiceBusca]}"]`) as HTMLElement | null;
    if (alvo) alvo.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [indiceBusca, totalResultados]); // eslint-disable-line react-hooks/exhaustive-deps

  const navegarBusca = (direcao: 1 | -1) => {
    if (totalResultados === 0) return;
    setIndiceBusca(prev => (prev + direcao + totalResultados) % totalResultados);
  };

  // Destaca o termo dentro de um texto
  const destacarTexto = (texto: string, termo: string): React.ReactNode => {
    if (!termo.trim() || termo.trim().length < 2) return texto;
    const regex = new RegExp(`(${termo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const partes = texto.split(regex);
    return partes.map((parte, i) =>
      regex.test(parte)
        ? <mark key={i} style={{ background: '#ffd54f', color: '#1a1a1a', borderRadius: 2, padding: '0 1px' }}>{parte}</mark>
        : parte
    );
  };

  // Renderiza conteúdo com links clicáveis (e destaque de busca nos trechos não-URL)
  const renderConteudoComLinks = (conteudo: string, termo: string): React.ReactNode => {
    const urlRegex = /(https?:\/\/[^\s<>"]+|www\.[^\s<>"]+)/gi;
    const partes = conteudo.split(urlRegex);
    const isUrl = /^(https?:\/\/|www\.)/i;
    return partes.map((parte, i) => {
      if (!parte) return null;
      if (isUrl.test(parte)) {
        const href = parte.startsWith('http') ? parte : `https://${parte}`;
        return (
          <a key={i} href={href} target="_blank" rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'underline', wordBreak: 'break-all' }}>
            {parte}
          </a>
        );
      }
      return <React.Fragment key={i}>{termo.trim().length >= 2 ? destacarTexto(parte, termo) : parte}</React.Fragment>;
    });
  };

  // Mantém refs atualizados a cada render
  conversaAtualRef.current = conversaAtual;
  onNaoLidasChangeRef.current = onNaoLidasChange;
  usuarioIdRef.current = usuario?.id;

  // Polling 1: re-busca todas as mensagens a cada 3s (sem otimizacao — garante atualização)
  useEffect(() => {
    if (!conversaAtual?.id) return;
    const conversaId = conversaAtual.id;
    const tick = async () => {
      try {
        const msgs = await chatInternoService.buscarMensagens(conversaId);
        if (msgs.length > 0) setMensagens(msgs);
      } catch {}
    };
    const interval = setInterval(tick, 3000);
    return () => clearInterval(interval);
  }, [conversaAtual?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling 2: atualiza sidebar e contadores a cada 5s (intervalo estável)
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const cidAtual = conversaAtualRef.current?.id ?? null;
        const lista = await chatInternoService.listarConversas();
        setConversas(lista.map(c => c.id === cidAtual ? { ...c, nao_lidas: 0 } : c));
        if (onNaoLidasChangeRef.current) {
          const total = lista.reduce((acc, c) => acc + (c.id === cidAtual ? 0 : Number(c.nao_lidas) || 0), 0);
          onNaoLidasChangeRef.current(total);
        }
      } catch {}
    }, 5000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const enviarMensagem = async () => {
    if ((!texto.trim() && !arquivo) || !conversaAtual || enviando) return;
    setEnviando(true);
    setErroEnvio('');
    try {
      const nova = await chatInternoService.enviarMensagem(conversaAtual.id, texto.trim(), arquivo || undefined);
      setMensagens(prev => [...prev, nova]);
      setTexto('');
      setArquivo(null);
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
      setConversas(prev => prev.map(c =>
        c.id === conversaAtual.id
          ? { ...c, ultima_mensagem: texto.trim() || (arquivo ? `📎 ${arquivo.name}` : ''), data_ultima_mensagem: new Date().toISOString() }
          : c
      ));
    } catch {
      setErroEnvio('Falha ao enviar. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviarMensagem();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        e.preventDefault();
        const file = items[i].getAsFile();
        if (!file) continue;
        setArquivo(file);
        break;
      }
    }
  };

  const excluirConversa = async (conversaId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmarExclusao(conversaId);
  };

  const confirmarExcluirConversa = async () => {
    if (!confirmarExclusao) return;
    try {
      await chatInternoService.excluirConversa(confirmarExclusao);
      setConversas(prev => prev.filter(c => c.id !== confirmarExclusao));
      if (conversaAtual?.id === confirmarExclusao) {
        setConversaAtual(null);
        setMensagens([]);
      }
    } catch {
      setErroEnvio('Erro ao excluir conversa.');
    } finally {
      setConfirmarExclusao(null);
    }
  };

  const adicionarMembroGrupo = async (usuarioId: string) => {
    if (!conversaAtual || adicionando) return;
    setAdicionando(true);
    try {
      await chatInternoService.adicionarMembro(conversaAtual.id, usuarioId);
      const lista = await chatInternoService.listarConversas();
      setConversas(lista);
      const atualizada = lista.find(c => c.id === conversaAtual.id);
      if (atualizada) setConversaAtual(atualizada);
      setBuscaNovoMembro('');
      setAdicionandoMembro(false);
    } catch (error) {
      console.error('Erro ao adicionar membro:', error);
    } finally {
      setAdicionando(false);
    }
  };

  const criarConversa = async () => {
    if (membrosSelecionados.length === 0) return;
    try {
      const res = await chatInternoService.criarConversa({
        tipo: novoTipo,
        nome: novoNome || undefined,
        membros: membrosSelecionados,
      });
      setMostrarNovaConversa(false);
      setMembrosSelecionados([]);
      setNovoNome('');
      setBuscaUsuario('');
      if (res.conversa) {
        const lista = await chatInternoService.listarConversas();
        setConversas(lista);
        const conv = lista.find(c => c.id === res.conversa.id) || res.conversa;
        setConversaAtual(conv);
      }
    } catch (error) {
      console.error('Erro ao criar conversa:', error);
    }
  };

  const abrirMiniPerfil = (remetenteId: string, remetenteNome: string, remetenteAvatar?: string) => {
    const u = usuarios.find(u => u.id === remetenteId);
    // Prefere remetenteAvatar (URL assinada vinda do chat) sobre u.avatar (chave R2 crua do contexto)
    setMiniPerfil({
      id: remetenteId,
      nome: u?.nome || remetenteNome,
      cargo: u?.cargo,
      departamento_id: u?.departamento_id || u?.departamentoId,
      avatar: remetenteAvatar || u?.avatar,
    });
  };

  const nomeConversa = (c: ChatConversa) => {
    if (c.tipo === 'grupo') return c.nome || 'Grupo';
    const outro = c.membros?.find(m => m.id !== usuario?.id);
    return outro?.nome || 'Conversa';
  };

  const usuariosFiltrados = usuarios.filter(u =>
    u.id !== usuario?.id &&
    (u.nome?.toLowerCase().includes(buscaUsuario.toLowerCase()) || u.email?.toLowerCase().includes(buscaUsuario.toLowerCase()))
  );

  const conversasFiltradas = conversas.filter(c =>
    nomeConversa(c).toLowerCase().includes(busca.toLowerCase())
  );

  const formatarHora = (data: string) => format(new Date(data), 'HH:mm', { locale: ptBR });
  const formatarData = (data: string) => {
    const d = new Date(data);
    const hoje = new Date();
    if (d.toDateString() === hoje.toDateString()) return formatarHora(data);
    return format(d, 'dd/MM', { locale: ptBR });
  };
  const formatarSeparadorData = (data: string): string => {
    const d = new Date(data);
    const hoje = new Date();
    const ontem = new Date(hoje);
    ontem.setDate(hoje.getDate() - 1);
    if (d.toDateString() === hoje.toDateString()) return 'Hoje';
    if (d.toDateString() === ontem.toDateString()) return 'Ontem';
    return format(d, "d 'de' MMMM 'de' yyyy", { locale: ptBR });
  };

  const containerStyle: React.CSSProperties = fullPage
    ? { display: 'flex', width: '100%', height: '100%', minHeight: 0, overflow: 'hidden' }
    : {
        position: 'fixed', bottom: 80, right: 16, width: 780, height: 520,
        background: cores.bg, borderRadius: 16, boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
        display: 'flex', zIndex: 1000, overflow: 'hidden',
      };

  return (
    <div style={containerStyle}>

      {/* ── Painel esquerdo — lista de conversas ── */}
      <div style={{ width: 260, borderRight: `1px solid ${cores.borda}`, display: 'flex', flexDirection: 'column', background: cores.bgPainel }}>
        <div style={{ padding: '12px 12px 8px', borderBottom: `1px solid ${cores.borda}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: cores.textoPrincipal }}>Mensagens</span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button onClick={() => setMostrarNovaConversa(true)} title="Nova conversa"
                style={{ background: '#e10600', border: 'none', borderRadius: 6, color: '#fff', padding: '4px 8px', cursor: 'pointer', fontSize: 13 }}>
                +
              </button>
              {!fullPage && (
                <button onClick={onFechar} style={{ background: 'none', border: 'none', cursor: 'pointer', color: cores.textoQuaternario, fontSize: 18 }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar conversa..."
            style={{ width: '100%', padding: '6px 10px', borderRadius: 8, border: `1px solid ${cores.borda}`, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {carregando ? (
            <div style={{ textAlign: 'center', padding: 20, color: cores.textoMuted, fontSize: 13 }}>Carregando...</div>
          ) : conversasFiltradas.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 20, color: cores.textoMuted, fontSize: 13 }}>Nenhuma conversa</div>
          ) : conversasFiltradas.map(c => {
            const outroMembro = c.tipo === 'direto' ? c.membros?.find(m => m.id !== usuario?.id) : null;
            const estaOnline = outroMembro ? usuariosOnline.includes(outroMembro.id) : false;
            return (
              <div key={c.id} onClick={() => setConversaAtual(c)}
                style={{
                  padding: '10px 12px', cursor: 'pointer', borderBottom: `1px solid ${cores.bordaSutil}`,
                  background: conversaAtual?.id === c.id ? cores.bg : 'transparent',
                  borderLeft: conversaAtual?.id === c.id ? '3px solid #e10600' : '3px solid transparent',
                }}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {/* Avatar com indicador online */}
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    {c.tipo === 'direto' && outroMembro ? (
                      <AvatarCircle nome={outroMembro.nome} avatar={outroMembro.avatar} id={outroMembro.id} size={38} />
                    ) : (
                      <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#fff">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                        </svg>
                      </div>
                    )}
                    {estaOnline && (
                      <span style={{ position: 'absolute', bottom: 0, right: 0, width: 10, height: 10, borderRadius: '50%', background: '#22c55e', border: `2px solid ${cores.bgPainel}` }} title="Online" />
                    )}
                  </div>

                  {/* Nome + prévia */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: c.nao_lidas > 0 ? 700 : 500, fontSize: 13, color: cores.textoPrincipal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {nomeConversa(c)}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, marginLeft: 4 }}>
                        <span style={{ fontSize: 11, color: cores.textoMuted }}>{c.data_ultima_mensagem ? formatarData(c.data_ultima_mensagem) : ''}</span>
                        <button onClick={e => excluirConversa(c.id, e)} title="Excluir"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: cores.textoMuted3, fontSize: 13, padding: '0 2px', lineHeight: 1 }}
                          onMouseEnter={e => (e.currentTarget.style.color = '#e10600')}
                          onMouseLeave={e => (e.currentTarget.style.color = cores.textoMuted3)}>
                          <svg xmlns="http://www.w3.org/2000/svg" width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
                      <span style={{ fontSize: 12, color: cores.textoQuaternario, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                        {c.ultima_mensagem ? (c.ultima_mensagem.length > 28 ? c.ultima_mensagem.slice(0, 28) + '...' : c.ultima_mensagem) : 'Sem mensagens'}
                      </span>
                      {c.nao_lidas > 0 && (
                        <span style={{ background: '#e10600', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 11, fontWeight: 700, marginLeft: 4 }}>
                          {c.nao_lidas}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Painel direito — mensagens ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {conversaAtual ? (
          <>
            {/* Header da conversa */}
            <div style={{ padding: '10px 16px', borderBottom: `1px solid ${cores.borda}`, background: cores.bg, display: 'flex', alignItems: 'center', gap: 10 }}>
              {/* Avatar no header — clicável para ampliar foto */}
              {conversaAtual.tipo === 'direto' && conversaAtual.membros?.find(m => m.id !== usuario?.id) ? (() => {
                const outro = conversaAtual.membros.find(m => m.id !== usuario?.id)!;
                return (
                  <div style={{ cursor: 'pointer', flexShrink: 0 }} title="Ver foto" onClick={() => setFotoExpandida({ nome: outro.nome, avatar: outro.avatar, id: outro.id })}>
                    <AvatarCircle nome={outro.nome} avatar={outro.avatar} id={outro.id} size={36} />
                  </div>
                );
              })() : (
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#fff">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                  </svg>
                </div>
              )}
              {/* Nome — clicável para mini perfil */}
              {conversaAtual.tipo === 'direto' && conversaAtual.membros?.find(m => m.id !== usuario?.id) ? (() => {
                const outro = conversaAtual.membros.find(m => m.id !== usuario?.id)!;
                return (
                  <button
                    onClick={() => abrirMiniPerfil(outro.id, outro.nome, outro.avatar)}
                    style={{ fontWeight: 700, fontSize: 15, color: cores.textoPrincipal, flex: 1, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0, fontFamily: 'inherit' }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#e10600')}
                    onMouseLeave={e => (e.currentTarget.style.color = cores.textoPrincipal)}
                  >
                    {nomeConversa(conversaAtual)}
                  </button>
                );
              })() : (
                <span style={{ fontWeight: 700, fontSize: 15, color: cores.textoPrincipal, flex: 1 }}>{nomeConversa(conversaAtual)}</span>
              )}
              {conversaAtual.tipo === 'grupo' && (
                <button
                  onClick={() => setMostrarInfoGrupo(true)}
                  style={{ background: cores.bgChip, border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 12, color: cores.textoTerciario, display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                  </svg>
                  {conversaAtual.membros?.length} membros
                </button>
              )}
              {/* Botão de busca */}
              <button
                onClick={() => setMostrarBusca(v => !v)}
                title="Buscar na conversa"
                style={{ background: mostrarBusca ? cores.bgChip : 'none', border: 'none', borderRadius: 8, padding: '6px 8px', cursor: 'pointer', color: mostrarBusca ? '#e10600' : cores.textoMuted4, display: 'flex', alignItems: 'center' }}
              >
                <svg width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                </svg>
              </button>
            </div>

            {/* Barra de busca */}
            {mostrarBusca && (
              <div style={{ padding: '6px 12px', borderBottom: `1px solid ${cores.borda}`, background: cores.bgSutil, display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width="15" height="15" fill="none" stroke={cores.textoMuted2} strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                  <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                </svg>
                <input
                  ref={buscaInputRef}
                  value={termoBusca}
                  onChange={e => setTermoBusca(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') navegarBusca(e.shiftKey ? -1 : 1);
                    if (e.key === 'Escape') { setMostrarBusca(false); setTermoBusca(''); }
                  }}
                  placeholder="Buscar na conversa..."
                  style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: cores.textoPrincipal }}
                />
                {termoBusca.trim().length >= 2 && (
                  <span style={{ fontSize: 11, color: cores.textoMuted4, whiteSpace: 'nowrap' }}>
                    {totalResultados === 0 ? 'Nenhum resultado' : `${indiceBusca + 1} de ${totalResultados}`}
                  </span>
                )}
                {totalResultados > 0 && (
                  <>
                    <button onClick={() => navegarBusca(-1)} title="Anterior (Shift+Enter)" style={{ background: 'none', border: 'none', cursor: 'pointer', color: cores.textoTerciario, padding: '2px 4px', fontSize: 14 }}>▲</button>
                    <button onClick={() => navegarBusca(1)} title="Próximo (Enter)" style={{ background: 'none', border: 'none', cursor: 'pointer', color: cores.textoTerciario, padding: '2px 4px', fontSize: 14 }}>▼</button>
                  </>
                )}
                <button onClick={() => { setMostrarBusca(false); setTermoBusca(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: cores.textoMuted2, fontSize: 16, padding: '0 2px' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            {/* Mensagens */}
            <div ref={mensagensContainerRef} style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(() => {
                let primeiraNaoLidaMarcada = false;
                let ultimaDataStr = '';
                return mensagens.map((m, indiceGlobal) => {
                if (!m.conteudo && !(m.anexos && m.anexos.length > 0)) return null;
                const dataMensagem = new Date(m.data_criacao).toDateString();
                const exibirSeparadorData = dataMensagem !== ultimaDataStr;
                if (exibirSeparadorData) ultimaDataStr = dataMensagem;
                const minha = m.remetente_id === usuario?.id;
                const ehNaoLida = !minha && minhaLeituraEntradaRef.current
                  ? new Date(m.data_criacao) > new Date(minhaLeituraEntradaRef.current)
                  : false;
                const ehPrimeiraNaoLida = ehNaoLida && !primeiraNaoLidaMarcada;
                if (ehPrimeiraNaoLida) primeiraNaoLidaMarcada = true;
                const membrosAtuais = conversas.find(c => c.id === conversaAtual?.id)?.membros || conversaAtual?.membros || [];
                const lida = minha && membrosAtuais
                  .filter(mb => mb.id !== usuario?.id)
                  .every(mb => mb.ultima_leitura && new Date(mb.ultima_leitura) >= new Date(m.data_criacao));
                const ehResultadoBusca = indicesMensagensBusca.includes(indiceGlobal);
                const ehResultadoAtual = ehResultadoBusca && indicesMensagensBusca[indiceBusca] === indiceGlobal;
                return (
                  <React.Fragment key={m.id}>
                    {exibirSeparadorData && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0 4px' }}>
                        <div style={{ flex: 1, height: 1, background: cores.borda }} />
                        <span style={{ fontSize: 11, color: cores.textoMuted5, fontWeight: 600, background: cores.bordaSutil, borderRadius: 10, padding: '3px 12px', whiteSpace: 'nowrap' }}>
                          {formatarSeparadorData(m.data_criacao)}
                        </span>
                        <div style={{ flex: 1, height: 1, background: cores.borda }} />
                      </div>
                    )}
                    {ehPrimeiraNaoLida && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0' }}>
                        <div style={{ flex: 1, height: 1, background: '#e10600' }} />
                        <span style={{ fontSize: 11, color: '#e10600', fontWeight: 700 }}>Novas mensagens</span>
                        <div style={{ flex: 1, height: 1, background: '#e10600' }} />
                      </div>
                    )}
                    <div
                      data-primeiro-nao-lido={ehPrimeiraNaoLida ? "true" : undefined}
                      data-busca-idx={indiceGlobal}
                      style={{ display: 'flex', justifyContent: minha ? 'flex-end' : 'flex-start', alignItems: 'flex-end', gap: 6, transition: 'box-shadow 0.3s', borderRadius: 8, padding: ehResultadoAtual || ehResultadoBusca ? '2px 4px' : undefined, boxShadow: ehResultadoAtual ? 'inset 0 0 0 2px #e10600' : ehResultadoBusca ? 'inset 0 0 0 1px rgba(225,6,0,0.4)' : undefined }}
                    >
                    {/* Avatar do remetente — clicável para ampliar foto */}
                    {!minha && (
                      <div
                        style={{ cursor: 'pointer', marginBottom: 18, flexShrink: 0 }}
                        title="Ver foto"
                        onClick={() => setFotoExpandida({ nome: m.remetente_nome, avatar: m.remetente_avatar, id: m.remetente_id })}
                      >
                        <AvatarCircle nome={m.remetente_nome} avatar={m.remetente_avatar} id={m.remetente_id} size={28} />
                      </div>
                    )}

                    <div style={{ maxWidth: '70%' }}>
                      {/* Nome clicável */}
                      {!minha && (
                        <button
                          onClick={() => abrirMiniPerfil(m.remetente_id, m.remetente_nome, m.remetente_avatar)}
                          style={{ fontSize: 11, color: cores.textoMuted, marginBottom: 2, marginLeft: 2, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit', display: 'block' }}
                          onMouseEnter={e => (e.currentTarget.style.color = '#e10600')}
                          onMouseLeave={e => (e.currentTarget.style.color = cores.textoMuted)}
                        >
                          {m.remetente_nome}
                        </button>
                      )}
                      <div style={{
                        background: minha ? '#e10600' : cores.bg,
                        color: minha ? '#fff' : cores.textoPrincipal,
                        borderRadius: minha ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                        padding: '8px 12px', fontSize: 13, lineHeight: 1.5,
                        border: minha ? 'none' : `1px solid ${cores.borda}`,
                      }}>
                        {m.conteudo && <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{renderConteudoComLinks(m.conteudo, termoBusca)}</div>}
                        {m.anexos?.map(a => (
                          <button key={a.id}
                            onClick={async () => {
                              try {
                                const res = await chatInternoService.downloadAnexo(a.id);
                                if (res.url) window.open(res.url, '_blank');
                              } catch { toast.error('Erro ao baixar anexo.'); }
                            }}
                            style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, color: minha ? '#ffcdd2' : '#e10600', fontSize: 12, textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ flexShrink: 0 }}>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
                            </svg>
                            {a.nome_original}
                          </button>
                        ))}
                      </div>
                      <div style={{ fontSize: 10, color: cores.textoMuted2, marginTop: 2, textAlign: minha ? 'right' : 'left', display: 'flex', alignItems: 'center', justifyContent: minha ? 'flex-end' : 'flex-start', gap: 3 }}>
                        {formatarHora(m.data_criacao)}
                        {minha && (
                          <span style={{ color: lida ? '#e10600' : cores.textoMuted2, fontSize: 12, fontWeight: 700 }} title={lida ? 'Visualizado' : 'Enviado'}>
                            {lida ? '✓✓' : '✓'}
                          </span>
                        )}
                      </div>
                    </div>
                    </div>
                  </React.Fragment>
                );
              });
            })()}
              <div ref={mensagensEndRef} />
            </div>

            {/* Input */}
            <div style={{ padding: '10px 12px', borderTop: `1px solid ${cores.borda}`, background: cores.bg }}>
              {arquivo && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, background: cores.bgPainel, borderRadius: 6, padding: '4px 8px' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={cores.textoTerciario} style={{ flexShrink: 0 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
                  </svg>
                  <span style={{ fontSize: 12, color: cores.textoTerciario, flex: 1 }}>{arquivo.name}</span>
                  <button onClick={() => setArquivo(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e10600', fontSize: 14 }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
              {erroEnvio && <div style={{ fontSize: 12, color: '#e10600', marginBottom: 4 }}>{erroEnvio}</div>}
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={e => { setArquivo(e.target.files?.[0] || null); e.target.value = ''; }} />
                <button onClick={() => fileInputRef.current?.click()} title="Anexar arquivo"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: cores.textoMuted, fontSize: 20, padding: 0, lineHeight: 1 }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
                  </svg>
                </button>
                <textarea
                  ref={textareaRef}
                  value={texto}
                  onChange={e => {
                    setTexto(e.target.value);
                    setErroEnvio('');
                    if (textareaRef.current) {
                      textareaRef.current.style.height = 'auto';
                      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
                    }
                  }}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  placeholder="Digite uma mensagem... (Ctrl+V para colar prints, Enter para enviar)"
                  rows={1}
                  style={{ flex: 1, border: `1px solid ${erroEnvio ? '#e10600' : cores.borda}`, borderRadius: 8, padding: '8px 12px', fontSize: 13, outline: 'none', resize: 'none', fontFamily: 'inherit', lineHeight: 1.4, overflowY: 'auto' }}
                />
                <button onClick={enviarMensagem} disabled={enviando || (!texto.trim() && !arquivo)}
                  style={{ background: '#e10600', border: 'none', borderRadius: 8, color: '#fff', padding: '8px 14px', cursor: enviando || (!texto.trim() && !arquivo) ? 'not-allowed' : 'pointer', opacity: enviando || (!texto.trim() && !arquivo) ? 0.5 : 1 }}>
                  {enviando ? '...' : '➤'}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, color: cores.textoMuted }}>
            <svg xmlns="http://www.w3.org/2000/svg" width={40} height={40} viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
            </svg>
            <div style={{ fontSize: 14 }}>Selecione uma conversa ou inicie uma nova</div>
            <button onClick={() => setMostrarNovaConversa(true)}
              style={{ background: '#e10600', border: 'none', borderRadius: 8, color: '#fff', padding: '8px 16px', cursor: 'pointer', fontSize: 13 }}>
              + Nova Conversa
            </button>
          </div>
        )}
      </div>

      {/* ── Modal nova conversa ── */}
      {mostrarNovaConversa && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
          <div style={{ background: cores.bg, borderRadius: 12, padding: 24, width: 380, maxHeight: '80%', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Nova Conversa</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setNovoTipo('direto')}
                style={{ flex: 1, padding: '6px 0', borderRadius: 6, border: 'none', background: novoTipo === 'direto' ? '#e10600' : cores.bgChip, color: novoTipo === 'direto' ? '#fff' : cores.textoSecundario, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                Direto
              </button>
              <button onClick={() => setNovoTipo('grupo')}
                style={{ flex: 1, padding: '6px 0', borderRadius: 6, border: 'none', background: novoTipo === 'grupo' ? '#e10600' : cores.bgChip, color: novoTipo === 'grupo' ? '#fff' : cores.textoSecundario, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                Grupo
              </button>
            </div>
            {novoTipo === 'grupo' && (
              <input value={novoNome} onChange={e => setNovoNome(e.target.value)} placeholder="Nome do grupo"
                style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${cores.borda}`, fontSize: 13, outline: 'none' }} />
            )}
            {novoTipo === 'grupo' && membrosSelecionados.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {usuarios.filter(u => membrosSelecionados.includes(u.id)).map(u => (
                  <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#1a1a2e', color: '#fff', borderRadius: 16, padding: '3px 10px', fontSize: 12 }}>
                    <span>{u.nome.split(' ')[0]}</span>
                    <button onClick={() => setMembrosSelecionados(prev => prev.filter(id => id !== u.id))}
                      style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1, display: 'flex' }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
            <input value={buscaUsuario} onChange={e => setBuscaUsuario(e.target.value)} placeholder="Buscar usuário..."
              style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${cores.borda}`, fontSize: 13, outline: 'none' }} />
            <div style={{ maxHeight: 200, overflowY: 'auto', border: `1px solid ${cores.borda}`, borderRadius: 8 }}>
              {usuariosFiltrados
                .filter(u => novoTipo === 'direto' || !membrosSelecionados.includes(u.id))
                .map(u => (
                  <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer', borderBottom: `1px solid ${cores.bordaSutil}` }}>
                    <input type={novoTipo === 'direto' ? 'radio' : 'checkbox'} name="membro"
                      checked={membrosSelecionados.includes(u.id)}
                      onChange={() => {
                        if (novoTipo === 'direto') {
                          setMembrosSelecionados([u.id]);
                        } else {
                          setMembrosSelecionados(prev => [...prev, u.id]);
                          setBuscaUsuario('');
                        }
                      }} />
                    <AvatarCircle nome={u.nome} avatar={u.avatar} id={u.id} size={28} />
                    <span style={{ fontSize: 13 }}>{u.nome}</span>
                    <span style={{ fontSize: 11, color: cores.textoMuted }}>{u.cargo}</span>
                  </label>
                ))}
              {usuariosFiltrados.filter(u => novoTipo === 'direto' || !membrosSelecionados.includes(u.id)).length === 0 && (
                <div style={{ padding: '12px', textAlign: 'center', fontSize: 13, color: cores.textoMuted }}>
                  {buscaUsuario ? 'Nenhum usuário encontrado' : 'Todos os usuários já foram adicionados'}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => { setMostrarNovaConversa(false); setMembrosSelecionados([]); }}
                style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${cores.borda}`, background: cores.bg, cursor: 'pointer', fontSize: 13 }}>
                Cancelar
              </button>
              <button onClick={criarConversa} disabled={membrosSelecionados.length === 0}
                style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#e10600', color: '#fff', cursor: 'pointer', fontSize: 13, opacity: membrosSelecionados.length === 0 ? 0.5 : 1 }}>
                Iniciar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal confirmação de exclusão ── */}
      {confirmarExclusao && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 20 }}>
          <div style={{ background: cores.bg, borderRadius: 12, padding: 24, width: 340, display: 'flex', flexDirection: 'column', gap: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="#e10600">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
              <span style={{ fontWeight: 700, fontSize: 16, color: cores.textoPrincipal }}>Excluir conversa</span>
            </div>
            <p style={{ fontSize: 13, color: cores.textoTerciario, margin: 0, lineHeight: 1.5 }}>
              Tem certeza que deseja excluir esta conversa? Esta ação não pode ser desfeita.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmarExclusao(null)}
                style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${cores.borda}`, background: cores.bg, cursor: 'pointer', fontSize: 13, color: cores.textoTerciario }}>
                Cancelar
              </button>
              <button onClick={confirmarExcluirConversa}
                style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#e10600', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal info do grupo ── */}
      {mostrarInfoGrupo && conversaAtual && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
          <div style={{ background: cores.bg, borderRadius: 12, padding: 24, width: 360, maxHeight: '80%', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg xmlns="http://www.w3.org/2000/svg" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                </svg>
                {nomeConversa(conversaAtual)}
              </span>
              <button onClick={() => { setMostrarInfoGrupo(false); setAdicionandoMembro(false); setBuscaNovoMembro(''); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: cores.textoQuaternario }}>
                <svg xmlns="http://www.w3.org/2000/svg" width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div>
              <p style={{ fontSize: 12, fontWeight: 600, color: cores.textoQuaternario, textTransform: 'uppercase', marginBottom: 8 }}>
                Membros ({conversaAtual.membros?.length})
              </p>
              <div style={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {conversaAtual.membros?.map(m => (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 8, background: cores.bgPainel }}>
                    <AvatarCircle nome={m.nome} avatar={m.avatar} id={m.id} size={28} />
                    <span style={{ fontSize: 13, color: cores.textoPrincipal }}>{m.nome}</span>
                    {m.id === usuario?.id && <span style={{ fontSize: 11, color: cores.textoMuted, marginLeft: 'auto' }}>Você</span>}
                  </div>
                ))}
              </div>
            </div>
            {!adicionandoMembro ? (
              <button onClick={() => setAdicionandoMembro(true)}
                style={{ padding: '8px', borderRadius: 8, border: `1px dashed ${cores.textoMuted3}`, background: cores.bg, cursor: 'pointer', fontSize: 13, color: cores.textoTerciario }}>
                + Adicionar membro
              </button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input
                  value={buscaNovoMembro}
                  onChange={e => setBuscaNovoMembro(e.target.value)}
                  placeholder="Buscar usuário para adicionar..."
                  autoFocus
                  style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${cores.borda}`, fontSize: 13, outline: 'none' }}
                />
                <div style={{ maxHeight: 160, overflowY: 'auto', border: `1px solid ${cores.borda}`, borderRadius: 8 }}>
                  {usuarios
                    .filter(u =>
                      u.id !== usuario?.id &&
                      !conversaAtual.membros?.some(m => m.id === u.id) &&
                      (u.nome?.toLowerCase().includes(buscaNovoMembro.toLowerCase()) || u.email?.toLowerCase().includes(buscaNovoMembro.toLowerCase()))
                    )
                    .map(u => (
                      <button key={u.id} onClick={() => adicionarMembroGrupo(u.id)} disabled={adicionando}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'none', border: 'none', borderBottom: `1px solid ${cores.bordaSutil}`, cursor: 'pointer', textAlign: 'left' }}>
                        <AvatarCircle nome={u.nome} avatar={u.avatar} id={u.id} size={28} />
                        <div>
                          <div style={{ fontSize: 13, color: cores.textoPrincipal }}>{u.nome}</div>
                          <div style={{ fontSize: 11, color: cores.textoMuted }}>{u.cargo}</div>
                        </div>
                      </button>
                    ))}
                </div>
                <button onClick={() => { setAdicionandoMembro(false); setBuscaNovoMembro(''); }}
                  style={{ padding: '6px', borderRadius: 8, border: `1px solid ${cores.borda}`, background: cores.bg, cursor: 'pointer', fontSize: 13, color: cores.textoQuaternario }}>
                  Cancelar
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Foto expandida (lightbox) ── */}
      {fotoExpandida && (
        <div
          style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 40 }}
          onClick={() => setFotoExpandida(null)}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }} onClick={e => e.stopPropagation()}>
            <AvatarCircle nome={fotoExpandida.nome} avatar={fotoExpandida.avatar} id={fotoExpandida.id} size={160} />
            <span style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>{fotoExpandida.nome}</span>
            <button
              onClick={() => setFotoExpandida(null)}
              style={{ color: '#fff', background: 'none', border: '1px solid rgba(255,255,255,0.35)', borderRadius: 8, padding: '6px 24px', cursor: 'pointer', fontSize: 13 }}
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      {/* ── Mini Perfil ── */}
      {miniPerfil && (
        <div
          style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 30 }}
          onClick={() => setMiniPerfil(null)}
        >
          <div
            style={{ background: cores.bg, borderRadius: 16, padding: '28px 24px', width: 260, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}
          >
            <AvatarCircle nome={miniPerfil.nome} avatar={miniPerfil.avatar} id={miniPerfil.id} size={72} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: cores.textoPrincipal }}>{miniPerfil.nome}</div>
              {miniPerfil.cargo && (
                <div style={{ fontSize: 13, color: cores.textoTerciario, marginTop: 4 }}>{miniPerfil.cargo}</div>
              )}
              {miniPerfil.departamento_id && (
                <div style={{ fontSize: 12, color: cores.textoMuted, marginTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
                  </svg>
                  <span>{obterDepartamentoPorId(miniPerfil.departamento_id)?.nome || 'Departamento não definido'}</span>
                </div>
              )}
            </div>
            <button
              onClick={() => setMiniPerfil(null)}
              style={{ padding: '6px 24px', borderRadius: 8, border: `1px solid ${cores.borda}`, background: cores.bg, cursor: 'pointer', fontSize: 13, color: cores.textoTerciario, width: '100%' }}
            >
              Fechar
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default ChatInterno;
